import type {
	FocusEventHandler,
	KeyboardEvent,
	MouseEvent,
	MouseEventHandler,
	ReactElement,
	ReactNode,
	Ref,
} from 'react';

import { type NewCoreIconProps } from '@atlaskit/icon';
import type { ContentProps, TriggerProps } from '@atlaskit/popup';
import type { Ati } from '@atlassian/ari/ati';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { CrossAppCreationConfig } from './crossAppCreation';
import type { UniversalCreateMenuSection } from './menu';
import type { SuggestionConfig } from './suggestion';

export type MenuControl = {
	close: () => void;
	reset: () => void;
};

export type CreateContentProps = (
	contentProps: ContentProps,
	menuControl: MenuControl,
) => ReactNode;

export type WrapperRenderProps = {
	// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- https://hello.jira.atlassian.cloud/browse/NAVX-179
	/**
	 * @deprecated You should not use this prop - this is made only for x-sell loom popup in Confluence
	 */
	triggerProps?: {
		'aria-controls'?: string;
		'aria-expanded'?: boolean;
		'aria-haspopup'?: boolean | 'dialog';
		'data-ds--level'?: string;
		onBlur?: FocusEventHandler;
		onFocus?: FocusEventHandler;
		onMouseEnter?: MouseEventHandler;
		onMouseLeave?: MouseEventHandler;
		ref?: Ref<any>;
	};
};

/**
 * Trigger props with splitButtonPrimaryRef for applying ref to trigger preloading functionality to primary button within split variant
 */
export interface TriggerPropsWithSplitPrimaryRef extends TriggerProps {
	splitButtonPrimaryRef?: React.Ref<any>; // Using any as ref can be assigned to any object
}

export type SourceProduct = UniversalCreateSourceProduct;

export type SuggestionLinkItemOpenTarget = '_self' | '_blank';

type MenuItemBase = {
	/**
	 * ATI for specified object types
	 */
	ati: Ati;

	/**
	 * The main textual content and label of the menu item.
	 */
	children: ReactNode;

	/**
	 * The icon before each item text
	 */
	elemBefore?: React.ComponentType<NewCoreIconProps>;

	/**
	 * An optional prop to decide a button or anchor to be rendered
	 */
	href?: string;

	/**
	 * Indicates that the menu item is disabled.
	 * It should not be used together with `href`
	 */
	isDisabled?: boolean;

	/**
	 * An optional prop to indicate the item is loading.
	 * Spotlight, onClick and some other functions will not be available
	 */
	isLoading?: boolean;

	/**
	 * An optional function that is called to load custom create content into the menu popup from @atlaskit/popup
	 * @param contentProps - the ContentProps from atlaskit/popup
	 * @param menuControl - menu controls to reset or close
	 * @returns React.ReactNode
	 */
	loadCreateContent?: CreateContentProps;

	/**
	 * Item onClick event that will be passed over to `MenuItem`
	 */
	onClick?: (event: MouseEvent | KeyboardEvent) => void;

	/**
	 * Optional prop to close the popup menu when an item is clicked.
	 * Default is set to true
	 */
	onClickCloseMenu?: boolean;

	/**
	 * Item onContextMenu event for right click that will be passed over to `MenuItem`
	 * @param event - The mouse right click event
	 * @returns void
	 */
	onContextMenu?: (event: MouseEvent) => void;

	/**
	 * Optional rel attribute for the link item
	 */
	rel?: string;

	/**
	 * An optional prop for href target
	 */
	target?: HTMLAnchorElement['target'];

	/**
	 * A unique string that appears as data attribute data-testid in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
};

type MenuItemWithRef = MenuItemBase & {
	/**
	 * An optional ref that will be attached to each menu item.
	 * It cannot be used together with `Wrapper`
	 */
	ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;

	/**
	 * Ensure Wrapper is not present
	 * @private
	 */
	Wrapper?: never;
};

type MenuItemWithWrapper = MenuItemBase & {
	// ensure ref is not present
	ref?: never;

	/**
	 * @private
	 * An optional component wrapper prop that can be used to wrap the menu item.
	 * This prop cannot be used together with `ref`
	 */
	Wrapper?: (props: { children: (renderProps?: WrapperRenderProps) => ReactNode }) => ReactElement;
};

export type MenuItem = MenuItemWithRef | MenuItemWithWrapper;

/**
 * Maximum of 10 items allowed per section
 */
export type SimpleMenuItems = MenuItem[];

/**
 * Threshold for show more button configuration is between 4 to 6 items. To be cleaned up after overflow experiment.
 * @private
 */
export type OverFlowThreshold = 4 | 5 | 6;

export const DEFAULT_OVERFLOW_THRESHOLD = 4;

/**
 * Type for foldable behavior configuration
 * - false/undefined: No folding behavior
 * - true: Standard folding with show more/less functionality
 * - number: New overflow behavior with no show less and scrollable container, show more button will be shown if items exceed this number
 */
export type FoldableType = boolean | OverFlowThreshold;

export type NamedItems = {
	/**
	 * It will be used as a show more/less button when items are more than 4. This button will not appear if items are less than 4.
	 * Up to 10 items will be shown. Default to false
	 * number will enable new overflow behaviour with no show less and a scrollable container. Show more button will be shown if items exceed this number
	 */
	foldable?: FoldableType;

	/**
	 * An optional section heading
	 */
	heading?: string;

	/**
	 * list of menu items to show
	 */
	items: SimpleMenuItems;
};

/**
 * Max of 3 sections allowed for phase 1.
 * In the future it will be reduced to 2 when `Suggestions` section be taken care from UC component
 */
export type MixedMenuItems = NamedItems[];

export type MenuSkeletonItemProps = {
	/**
	 * Height (in pixels) of skeleton avatar - only used in V1 skeleton item
	 * Default height used if not provided
	 */
	avatarHeight?: number;
	/**
	 * Height (in pixels) of skeleton content bar
	 * Default height used if not provided
	 */
	contentHeight?: number;
	/**
	 * Optional test ID for the skeleton item
	 * Added as data-testid attribute to root element
	 */
	testId?: string;
};

export type UniversalCreateItems = SimpleMenuItems | MixedMenuItems;

export interface MenuControlContextProps extends MenuControl {
	load: (loadCreateContent: CreateContentProps) => void;
}

export interface MenuStateContextProps {
	crossAppCreationConfig: CrossAppCreationConfig;
	growthRecommendationsSessionId?: string;
	isNav4Enabled?: boolean;
	items: MixedMenuItems;
	preventClose?: boolean;

	/**
	 * The primary action item from the split button trigger.
	 * On small viewports (sm breakpoint and below), this item is prepended
	 * to the first menu section since the split button is not displayed.
	 */
	primaryActionItem?: MenuItem;

	product: SourceProduct;
	sections: UniversalCreateMenuSection[];
	/** Optional props */
	selectedItem?: Ati;
	setGrowthRecommendationsSessionId?: (sessionId: string | undefined) => void;
	subProduct?: string;
	suggestionConfig: SuggestionConfig;
}
